import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { mediaQueryLarge } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Mobile-only behavior for product pages using grid presentation:
    // keep slide order intact and programmatically move to slide 2 after mount.
    this.#jumpToSecondSlideOnMobile();
  }

  #controller = new AbortController();
  #hasAppliedMobileSecondImage = false;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  #jumpToSecondSlideOnMobile = () => {
    if (this.#hasAppliedMobileSecondImage) return;
    if (mediaQueryLarge.matches) return;
    if (this.presentation !== 'grid') return;
    if (this.dataset.defaultSecondImage !== 'true') return;

    const run = (attempt = 0) => {
      const slideshow = this.slideshow;
      if (!slideshow) return;

      const dots = slideshow.querySelectorAll('.slideshow-controls__dots .slideshow-control');
      const secondDot = dots?.[1];

      if (secondDot instanceof HTMLElement) {
        secondDot.click();
      } else if ((slideshow.slides?.length || 0) > 1) {
        // Fallback when dots are not used (counter/hint/thumbnails)
        slideshow.select(1, undefined, { animate: false });
      }

      // Ensure controls reflect slide 2 as selected.
      if (slideshow.current === 1) {
        this.#hasAppliedMobileSecondImage = true;
        return;
      }

      // Retry a few times because slideshow + controls can initialize asynchronously on mobile.
      if (attempt < 8) {
        setTimeout(() => run(attempt + 1), 60);
      }
    };

    requestAnimationFrame(() => requestAnimationFrame(run));
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
